import moment from "moment";
import { useEffect, useMemo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import Select from "react-select";
import { trimDecimals } from "../../../utils/trim-decimal";

const StoreAllotBatchModal = ({
    row,
    batches,
    loadingBatches,
    index,
    updateBatch,
}) => {
    const {
        control,
        register,
        formState: { errors },
        watch,
        setValue,
        setError,
        clearErrors,
    } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `slips.${index}.batches`,
    });

    const watchedBatches = watch(`slips.${index}.batches`);

    const totalAllotted = trimDecimals(
        watchedBatches?.reduce(
            (sum, field) =>
                sum + trimDecimals(Number(field?.allotted_quantity || 0), 5),
            0
        ),
        5
    );

    const toAllotQuantity = useMemo(() => {
        const val = trimDecimals(
            Number(row?.requested_quantity || 0) -
                Number(row?.received_quantity || 0) -
                totalAllotted,
            5
        );
        return Math.max(0, val);
    }, [totalAllotted, row?.requested_quantity, row?.received_quantity]);

    useEffect(() => {
        const available = Number(row?.stock_quantity || 0);
        const requested = Number(row?.requested_quantity || 0);
        const received = Number(row?.received_quantity || 0);
        const totalAllocated = trimDecimals(totalAllotted + received, 5);

        if (totalAllotted > available) {
            setError(`slips.${index}.invalid_allotment`, {
                type: "manual",
                message: `Total allotted (${totalAllocated}) exceeds available stock (${available})`,
            });
        } else if (totalAllocated > requested) {
            setError(`slips.${index}.invalid_allotment`, {
                type: "manual",
                message: `Total allotted (${totalAllocated}) exceeds requested quantity (${requested})`,
            });
        } else {
            clearErrors(`slips.${index}.invalid_allotment`);
        }
    }, [
        totalAllotted,
        row?.stock_quantity,
        row?.requested_quantity,
        row?.received_quantity,
        setError,
        clearErrors,
        index,
    ]);

    useEffect(() => {
        return () => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
                document.body.classList.remove("modal-open");
                document.body.style = "";
            }
        };
    }, []);

    return (
        <>
            <div
                className="modal fade m-0 p-0"
                tabIndex={-1}
                id={`allocate-batch-store-modal-${row?.id}`}
                aria-modal
            >
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                        >
                            <em
                                className="icon ni ni-cross"
                                aria-hidden="true"
                            ></em>
                        </button>
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Allocate{" "}
                                {row?.category === "WIP"
                                    ? "Daily Plans"
                                    : "Batch"}
                            </h5>
                        </div>
                        <div className="modal-body">
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <p>
                                        <strong className="me-2">Item:</strong>
                                        {row?.item_name}
                                    </p>
                                </div>
                                <div className="col-4">
                                    <p>
                                        <strong className="me-2">
                                            Available Stock:
                                        </strong>
                                        {trimDecimals(row?.stock_quantity, 5)}
                                    </p>
                                </div>
                                <div className="col-4">
                                    <p>
                                        <strong className="me-2">
                                            Requested Quantity:
                                        </strong>
                                        {row?.requested_quantity}
                                    </p>
                                </div>
                                <div className="col-4">
                                    <p>
                                        <strong className="me-2">
                                            Received Quantity:
                                        </strong>
                                        {row?.received_quantity}
                                    </p>
                                </div>
                                <div className="col-4">
                                    <p className="mb-0">
                                        <strong className="me-2">
                                            Allotted Stock:
                                        </strong>
                                        {totalAllotted.toFixed(4)}
                                    </p>
                                    <p className="text-danger">
                                        {errors?.slips?.[index]
                                            ?.invalid_allotment &&
                                            errors?.slips?.[index]
                                                ?.invalid_allotment?.message}
                                    </p>
                                </div>
                                <div className="col-4">
                                    <p className="mb-0">
                                        <strong className="me-2">
                                            Remaining to Allot:
                                        </strong>
                                        <span className="text-primary">
                                            {toAllotQuantity.toFixed(4)}
                                        </span>
                                    </p>
                                </div>
                            </div>
                            {fields?.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-bordered mb-3">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "40%" }}>
                                                    {row?.category === "WIP"
                                                        ? "Daily Plans"
                                                        : "Batch"}
                                                    <small className="text-danger ms-1">
                                                        *
                                                    </small>
                                                </th>
                                                <th style={{ width: "40%" }}>
                                                    Quantity
                                                    <small className="text-danger ms-1">
                                                        *
                                                    </small>
                                                </th>
                                                <th style={{ width: "20%" }}>
                                                    Remove
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fields.map((field, i) => {
                                                const basePath = `slips.${index}.batches.${i}`;
                                                return (
                                                    <tr key={field.id}>
                                                        <td>
                                                            <Controller
                                                                name={`${basePath}.batch`}
                                                                rules={{
                                                                    required: `${
                                                                        row?.category ===
                                                                        "WIP"
                                                                            ? "Daily plan"
                                                                            : "Batch"
                                                                    } is required`,
                                                                }}
                                                                control={
                                                                    control
                                                                }
                                                                render={({
                                                                    field,
                                                                }) => {
                                                                    // build list of already selected batch values from other slips (exclude current index)
                                                                    const selectedBatches =
                                                                        watchedBatches
                                                                            .filter(
                                                                                (
                                                                                    _,
                                                                                    idx
                                                                                ) =>
                                                                                    idx !==
                                                                                    i
                                                                            )
                                                                            .map(
                                                                                (
                                                                                    f
                                                                                ) =>
                                                                                    f
                                                                                        .batch
                                                                                        ?.value
                                                                            )
                                                                            .filter(
                                                                                Boolean
                                                                            );

                                                                    // get raw batches for the item, default empty array
                                                                    const rawBatches =
                                                                        batches?.[
                                                                            row
                                                                                ?.item_id
                                                                        ] || [];

                                                                    // copy + sort oldest-first by created_at (same logic as before)
                                                                    const sortedBatches =
                                                                        rawBatches
                                                                            .slice()
                                                                            .sort(
                                                                                (
                                                                                    a,
                                                                                    b
                                                                                ) => {
                                                                                    const dateA =
                                                                                        moment(
                                                                                            a?.created_at
                                                                                        );
                                                                                    const dateB =
                                                                                        moment(
                                                                                            b?.created_at
                                                                                        );
                                                                                    return dateA.isValid() &&
                                                                                        dateB.isValid()
                                                                                        ? dateA.diff(
                                                                                              dateB
                                                                                          )
                                                                                        : 0;
                                                                                }
                                                                            );

                                                                    // precompute remaining quantities for each batch (and determine disabling rules)
                                                                    const optionsWithMeta =
                                                                        sortedBatches.map(
                                                                            (
                                                                                b,
                                                                                idx
                                                                            ) => {
                                                                                const receivedQty =
                                                                                    Number(
                                                                                        b?.received_quantity ??
                                                                                            0
                                                                                    );

                                                                                const usedQty =
                                                                                    Number(
                                                                                        b?.used_quantity ??
                                                                                            b?.allocated_quantity ??
                                                                                            b?.consumed_quantity ??
                                                                                            b?.usedQty ??
                                                                                            0
                                                                                    );

                                                                                const remainingQty =
                                                                                    trimDecimals(
                                                                                        receivedQty -
                                                                                            usedQty,
                                                                                        5
                                                                                    );

                                                                                let prevRemaining = 0;
                                                                                if (
                                                                                    idx >
                                                                                    0
                                                                                ) {
                                                                                    const prev =
                                                                                        sortedBatches[
                                                                                            idx -
                                                                                                1
                                                                                        ];
                                                                                    const prevReceived =
                                                                                        Number(
                                                                                            prev?.received_quantity ??
                                                                                                0
                                                                                        );
                                                                                    const prevUsed =
                                                                                        Number(
                                                                                            prev?.used_quantity ??
                                                                                                prev?.allocated_quantity ??
                                                                                                prev?.consumed_quantity ??
                                                                                                prev?.usedQty ??
                                                                                                0
                                                                                        ) ||
                                                                                        0;
                                                                                    prevRemaining =
                                                                                        trimDecimals(
                                                                                            prevReceived -
                                                                                                prevUsed,
                                                                                            5
                                                                                        );
                                                                                }

                                                                                const isDisabled =
                                                                                    remainingQty <=
                                                                                        0 ||
                                                                                    (idx >
                                                                                        0 &&
                                                                                        prevRemaining >
                                                                                            0);

                                                                                return {
                                                                                    item_id:
                                                                                        row?.item_id,
                                                                                    qnt: receivedQty,
                                                                                    value: b?.batch_number,
                                                                                    label: `${
                                                                                        b?.batch_number
                                                                                    } : ${
                                                                                        b?.created_at &&
                                                                                        moment(
                                                                                            b?.created_at
                                                                                        ).isValid()
                                                                                            ? moment(
                                                                                                  b.created_at
                                                                                              ).format(
                                                                                                  "DD-MM-YYYY"
                                                                                              )
                                                                                            : "N/A"
                                                                                    } : Qty ${
                                                                                        receivedQty?.toFixed?.(
                                                                                            4
                                                                                        ) ??
                                                                                        receivedQty
                                                                                    }${` (Remaining ${
                                                                                        remainingQty?.toFixed?.(
                                                                                            4
                                                                                        ) ??
                                                                                        remainingQty
                                                                                    })`}`,
                                                                                    isDisabled,
                                                                                    remainingQty,
                                                                                    __raw: b,
                                                                                };
                                                                            }
                                                                        );

                                                                    // filter out already selected batches (by .value)
                                                                    const availableOptions =
                                                                        optionsWithMeta.filter(
                                                                            (
                                                                                option
                                                                            ) =>
                                                                                !selectedBatches.includes(
                                                                                    option.value
                                                                                )
                                                                        );

                                                                    return (
                                                                        <>
                                                                            <Select
                                                                                {...field}
                                                                                options={
                                                                                    availableOptions
                                                                                }
                                                                                isLoading={
                                                                                    loadingBatches
                                                                                }
                                                                                styles={{
                                                                                    menuPortal:
                                                                                        (
                                                                                            base
                                                                                        ) => ({
                                                                                            ...base,
                                                                                            zIndex: 9999,
                                                                                        }),
                                                                                    menu: (
                                                                                        base
                                                                                    ) => ({
                                                                                        ...base,
                                                                                        zIndex: 9999,
                                                                                    }),
                                                                                }}
                                                                                menuPortalTarget={
                                                                                    document.body
                                                                                }
                                                                                placeholder={`Select a ${
                                                                                    row?.category ===
                                                                                    "WIP"
                                                                                        ? "daily plan"
                                                                                        : "batch"
                                                                                }`}
                                                                                // prevent selection of disabled options (react-select reads this prop)
                                                                                isOptionDisabled={(
                                                                                    option
                                                                                ) =>
                                                                                    Boolean(
                                                                                        option?.isDisabled
                                                                                    )
                                                                                }
                                                                                onChange={(
                                                                                    val
                                                                                ) => {
                                                                                    field.onChange(
                                                                                        val
                                                                                    );

                                                                                    const optionRemaining =
                                                                                        typeof val?.remainingQty ===
                                                                                        "number"
                                                                                            ? val.remainingQty
                                                                                            : val?.qnt ??
                                                                                              0;

                                                                                    const suggestedQty =
                                                                                        trimDecimals(
                                                                                            Math.min(
                                                                                                toAllotQuantity,
                                                                                                optionRemaining ||
                                                                                                    0
                                                                                            ),
                                                                                            5
                                                                                        );

                                                                                    setValue(
                                                                                        `slips.${index}.batches.${i}.allotted_quantity`,
                                                                                        suggestedQty,
                                                                                        {
                                                                                            shouldValidate: true,
                                                                                            shouldDirty: true,
                                                                                        }
                                                                                    );

                                                                                    setTimeout(
                                                                                        () => {
                                                                                            updateBatch();
                                                                                        },
                                                                                        0
                                                                                    );
                                                                                }}
                                                                                // ensure the select shows the label/value correctly when controlled value is an object
                                                                                getOptionLabel={(
                                                                                    option
                                                                                ) =>
                                                                                    option.label ??
                                                                                    option.value
                                                                                }
                                                                                getOptionValue={(
                                                                                    option
                                                                                ) =>
                                                                                    option.value ??
                                                                                    option
                                                                                }
                                                                                // if you want disabled options to be visibly styled, react-select supports that via isOptionDisabled
                                                                                // We still pass the isDisabled flag inside option objects for debugging or other uses
                                                                                menuPlacement="auto"
                                                                                menuPosition="fixed"
                                                                            />
                                                                            {errors
                                                                                ?.slips?.[
                                                                                index
                                                                            ]
                                                                                ?.batches?.[
                                                                                i
                                                                            ]
                                                                                ?.batch
                                                                                ?.message && (
                                                                                <span className="fs-8 text-danger">
                                                                                    {
                                                                                        errors
                                                                                            ?.slips?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.batches?.[
                                                                                            i
                                                                                        ]
                                                                                            ?.batch
                                                                                            ?.message
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }}
                                                            />
                                                        </td>

                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                step={
                                                                    row?.category ===
                                                                    "WIP"
                                                                        ? 1
                                                                        : "any"
                                                                }
                                                                {...register(
                                                                    `${basePath}.allotted_quantity`,
                                                                    {
                                                                        valueAsNumber: true,
                                                                        required:
                                                                            "Quantity is required",
                                                                        min: {
                                                                            value: 0.00001,
                                                                            message:
                                                                                "Quantity must be greater than 0",
                                                                        },
                                                                        validate:
                                                                            (
                                                                                val
                                                                            ) => {
                                                                                const qnt =
                                                                                    watch(
                                                                                        `slips.${index}.batches.${i}.batch`
                                                                                    )?.qnt;

                                                                                if (
                                                                                    row?.category ===
                                                                                        "WIP" &&
                                                                                    !Number.isInteger(
                                                                                        val
                                                                                    )
                                                                                ) {
                                                                                    return "Quantity must be an integer for WIPs";
                                                                                }
                                                                                if (
                                                                                    qnt ===
                                                                                        undefined ||
                                                                                    qnt ===
                                                                                        null
                                                                                )
                                                                                    return true;
                                                                                if (
                                                                                    val >
                                                                                    qnt
                                                                                )
                                                                                    return `Quantity cannot exceed available ${
                                                                                        row?.category ===
                                                                                        "WIP"
                                                                                            ? "daily plan"
                                                                                            : "batch"
                                                                                    } value (${qnt.toFixed(
                                                                                        4
                                                                                    )})`;
                                                                                return true;
                                                                            },
                                                                    }
                                                                )}
                                                                onBlur={() => {
                                                                    updateBatch();
                                                                }}
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    // Allow onChange to update the value
                                                                    const val =
                                                                        e.target
                                                                            .value;
                                                                    setValue(
                                                                        `${basePath}.allotted_quantity`,
                                                                        val ===
                                                                            ""
                                                                            ? 0
                                                                            : Number(
                                                                                  val
                                                                              ),
                                                                        {
                                                                            shouldValidate: false,
                                                                            shouldDirty: true,
                                                                        }
                                                                    );
                                                                }}
                                                                disabled={
                                                                    !watch(
                                                                        `slips.${index}.batches.${i}.batch`
                                                                    )
                                                                }
                                                            />
                                                            {errors?.slips?.[
                                                                index
                                                            ]?.batches?.[i]
                                                                ?.allotted_quantity
                                                                ?.message && (
                                                                <span className="fs-8 text-danger">
                                                                    {
                                                                        errors
                                                                            ?.slips?.[
                                                                            index
                                                                        ]
                                                                            ?.batches?.[
                                                                            i
                                                                        ]
                                                                            ?.allotted_quantity
                                                                            ?.message
                                                                    }
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => {
                                                                    remove(i);
                                                                    // Update after removal
                                                                    setTimeout(
                                                                        () => {
                                                                            updateBatch();
                                                                        },
                                                                        0
                                                                    );
                                                                }}
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() =>
                                    append({
                                        allotted_quantity: 0,
                                        batch: null,
                                    })
                                }
                            >
                                + Add Batch
                            </button>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light"
                                data-bs-dismiss="modal"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ position: "relative", display: "inline-block" }}>
                {(errors?.slips?.[index]?.batches?.some(
                    (f) => f?.batch?.message || f?.allotted_quantity?.message
                ) ||
                    errors?.slips?.[index]?.invalid_allotment) && (
                    <div
                        style={{
                            height: "10px",
                            width: "10px",
                            borderRadius: "50%",
                            backgroundColor: "red",
                            position: "absolute",
                            top: "-2px",
                            left: "-2px",
                            zIndex: 10,
                        }}
                    />
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    data-bs-toggle="modal"
                    data-bs-target={`#allocate-batch-store-modal-${row?.id}`}
                >
                    Allocate
                </button>
            </div>
        </>
    );
};

export default StoreAllotBatchModal;
